// ─────────────────────────────────────────────────────────────────────────────
// V2Account — account pages in the v2 design: My profile, Bank details, Team.
// Endpoints are v1's:
//   Profile: POST /api/auth/getuser, GET /api/company, PUT /api/company/update (owner/admin),
//            GET|PUT /api/irn/credentials (secret/password masked on GET; only sent when changed),
//            PUT /api/auth/changepassword
//   Bank:    GET via bankContext.getBankDetails, POST /api/bank/addbankdetails (upsert)
//   Team:    GET /api/auth/team, POST /team/invite | /team/create, PUT /team/:id/role | /status,
//            POST /team/:id/resend-invite, DELETE /team/:id
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import bankContext from "../context/bank/bankContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Segmented, Sheet, Skel, Switch } from "./primitives";
import { Field, PageHead } from "./kit";

const tok = () => localStorage.getItem("token");
const auth = () => ({ "auth-token": tok() });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const ini = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const RX = {
  gstin: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
  ifsc: /^[A-Z]{4}0[A-Z0-9]{6}$/,
  upi: /^[\w.-]+@[\w.-]+$/,
  pin: /^[1-9][0-9]{5}$/,
};
const Banner = ({ tone = "bad", children }) => <div className={`v2-banner v2-banner--${tone}`}><Glyph name={tone === "good" ? "check" : "alert"} size={16} /> {children}</div>;

// Floating glass save bar for long forms: appears only when something changed.
function SaveBar({ dirty, busy, onSave, onReset, label = "Save changes" }) {
  return (
    <div className={`v2cm-bar v2-glass v2ac-savebar${dirty ? " is-on" : ""}`} aria-hidden={!dirty}>
      <span>Unsaved changes</span>
      <button type="button" className="v2-btn v2-btn--sm" tabIndex={dirty ? 0 : -1} onClick={onReset}>Discard</button>
      <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" tabIndex={dirty ? 0 : -1} disabled={busy} onClick={onSave}>{busy ? "Saving…" : label}</button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MY PROFILE
// ══════════════════════════════════════════════════════════════════════════════
const EMPTY_CO = { companyName: "", companyGSTIN: "", companyAddress: "", companyPhone: "", companyEmail: "", businessType: "service", legalName: "", tradeName: "", pan: "", address: { line1: "", line2: "", city: "", pincode: "", state: "", stateCode: "" } };
const fromCompany = (d = {}) => ({
  ...EMPTY_CO, ...Object.fromEntries(Object.keys(EMPTY_CO).filter((k) => k !== "address").map((k) => [k, d[k] || EMPTY_CO[k]])),
  address: { ...EMPTY_CO.address, ...Object.fromEntries(Object.keys(EMPTY_CO.address).map((k) => [k, d.address?.[k] || ""])) },
});

export function V2Profile({ showAlert }) {
  usePageTitle("My profile");
  const { isAdmin } = useContext(userContext);
  const canEdit = isAdmin();
  const [tab, setTab] = useState("company");
  const [me, setMe] = useState(null);
  const [saved, setSaved] = useState(null);
  const [co, setCo] = useState(EMPTY_CO);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/auth/getuser`, { method: "POST", headers: json() }).then((r) => r.json()).then(setMe).catch(() => setMe({}));
    fetch(`${API_BASE}/api/company`, { headers: auth() }).then((r) => r.json()).then((d) => { const c = fromCompany(d); setSaved(c); setCo(c); }).catch(() => { setSaved(EMPTY_CO); });
  }, []);
  const dirty = saved && JSON.stringify(saved) !== JSON.stringify(co);
  const set = (k) => (e) => { const v = e?.target ? e.target.value : e; setCo((c) => ({ ...c, [k]: v })); };
  const setA = (k) => (e) => setCo((c) => ({ ...c, address: { ...c.address, [k]: e.target.value } }));
  const gstin = co.companyGSTIN.trim().toUpperCase();
  const issues = [
    gstin && !RX.gstin.test(gstin) && "GSTIN doesn't look valid (15 characters, e.g. 22AAAAA0000A1Z5).",
    co.pan && !RX.pan.test(co.pan.toUpperCase()) && "PAN doesn't look valid (e.g. ABCDE1234F).",
    gstin && co.pan && RX.gstin.test(gstin) && gstin.slice(2, 12) !== co.pan.toUpperCase() && "PAN doesn't match the PAN inside your GSTIN.",
    co.address.pincode && !RX.pin.test(co.address.pincode) && "Pincode should be 6 digits.",
  ].filter(Boolean);

  const save = async () => {
    setErr("");
    if (!co.companyName.trim()) { setErr("Company name is required."); return; }
    setBusy(true);
    try {
      const body = { ...co, companyGSTIN: gstin, pan: co.pan.toUpperCase(),
        address: { ...co.address, stateCode: co.address.stateCode || (gstin.length >= 2 ? gstin.slice(0, 2) : "") } };
      const r = await fetch(`${API_BASE}/api/company/update`, { method: "PUT", headers: json(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.message || "Couldn't save company details");
      setSaved(body); setCo(body); showAlert?.("Company details saved", "success");
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };

  return (
    <div className="v2p v2ac">
      <section className="v2-card v2ac-hero">
        {!saved ? <Skel h={88} r={22} /> : <>
          <span className="v2-avatar v2ac-avatar">{ini(co.companyName || me?.name)}</span>
          <div className="v2ac-hero-text">
            <span className="v2k-eyebrow">My profile</span>
            <h1>{saved.companyName || "Your company"}</h1>
            <div className="v2ac-meta">
              {saved.companyGSTIN && <span className="v2rp-mono">{saved.companyGSTIN}</span>}
              {me?.email && <span><Glyph name="user" size={13} /> {me.name ? `${me.name} · ` : ""}{me.email}</span>}
              {me?.role && <span className="v2-status v2-status--info">{me.role[0].toUpperCase() + me.role.slice(1)}</span>}
            </div>
          </div>
        </>}
      </section>
      <div className="v2rp-tabs v2ac-tabs">
        {[["company", "Company"], ["irn", "e-Invoice (IRN)"], ["security", "Security"]].map(([k, l]) => (
          <button key={k} type="button" className={`v2-chip-btn v2i-preset${tab === k ? " is-on" : ""}`} aria-pressed={tab === k} onClick={() => setTab(k)}>{l}</button>))}
      </div>

      {tab === "company" && (
        <div className="v2ac-stack">
          {!canEdit && <Banner tone="warn">Only the owner or an admin can change company details.</Banner>}
          <fieldset className="v2ac-fs v2ac-grid" disabled={!canEdit || !saved}>
            <section className="v2-card v2ac-section">
              <header><h3>Business</h3><span>How your company appears on invoices</span></header>
              <div className="v2-form">
                <Field label="Company name"><input className="v2-input" value={co.companyName} onChange={set("companyName")} /></Field>
                <div className="v2-form-row">
                  <Field label="Legal name" optional hint="Must match GST records exactly — used for e-invoices"><input className="v2-input" value={co.legalName} onChange={set("legalName")} /></Field>
                  <Field label="Trade name" optional hint="Leave blank if same as legal name"><input className="v2-input" value={co.tradeName} onChange={set("tradeName")} /></Field>
                </div>
                <Field label="You mostly sell" hint="Controls the invoice format">
                  <Segmented ariaLabel="Business type" value={co.businessType} onChange={set("businessType")} options={[{ value: "service", label: "Services" }, { value: "goods", label: "Goods" }]} />
                </Field>
              </div>
            </section>
            <section className="v2-card v2ac-section">
              <header><h3>Tax</h3><span>GST registration and PAN</span></header>
              <div className="v2-form">
                <div className="v2-form-row">
                  <Field label="GSTIN" optional><input className="v2-input v2rp-mono" maxLength={15} value={co.companyGSTIN} onChange={(e) => set("companyGSTIN")(e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" /></Field>
                  <Field label="PAN" optional><input className="v2-input v2rp-mono" maxLength={10} value={co.pan} onChange={(e) => set("pan")(e.target.value.toUpperCase())} placeholder="ABCDE1234F" /></Field>
                </div>
                {issues.length > 0 && <div className="v2ac-issues">{issues.map((i) => <span key={i}><Glyph name="alert" size={14} /> {i}</span>)}</div>}
              </div>
            </section>
            <section className="v2-card v2ac-section">
              <header><h3>Contact</h3><span>Shown on invoices and the client portal</span></header>
              <div className="v2-form">
                <div className="v2-form-row">
                  <Field label="Email" optional><input className="v2-input" type="email" value={co.companyEmail} onChange={set("companyEmail")} placeholder="contact@yourcompany.com" /></Field>
                  <Field label="Phone" optional><input className="v2-input" type="tel" value={co.companyPhone} onChange={set("companyPhone")} placeholder="+91 98765 43210" /></Field>
                </div>
                <Field label="Address on invoices" optional><textarea className="v2-input v2ac-textarea" rows={3} value={co.companyAddress} onChange={set("companyAddress")} /></Field>
              </div>
            </section>
            <section className="v2-card v2ac-section">
              <header><h3>Registered address</h3><span>Structured address used for e-invoices (IRN)</span></header>
              <div className="v2-form">
                <div className="v2-form-row">
                  <Field label="Address line 1"><input className="v2-input" value={co.address.line1} onChange={setA("line1")} /></Field>
                  <Field label="Address line 2" optional><input className="v2-input" value={co.address.line2} onChange={setA("line2")} /></Field>
                </div>
                <div className="v2ac-row4">
                  <Field label="City"><input className="v2-input" value={co.address.city} onChange={setA("city")} /></Field>
                  <Field label="Pincode"><input className="v2-input" inputMode="numeric" maxLength={6} value={co.address.pincode} onChange={setA("pincode")} /></Field>
                  <Field label="State"><input className="v2-input" value={co.address.state} onChange={setA("state")} /></Field>
                  <Field label="State code" hint={!co.address.stateCode && gstin.length >= 2 ? `${gstin.slice(0, 2)} from GSTIN` : undefined}><input className="v2-input" maxLength={2} value={co.address.stateCode} onChange={setA("stateCode")} placeholder={gstin.slice(0, 2) || "—"} /></Field>
                </div>
              </div>
            </section>
          </fieldset>
          {err && <Banner>{err}</Banner>}
          <SaveBar dirty={!!dirty && canEdit} busy={busy} onSave={save} onReset={() => { setCo(saved); setErr(""); }} />
        </div>
      )}
      {tab === "irn" && <GSPCredentials showAlert={showAlert} canEdit={canEdit} />}
      {tab === "security" && <Security showAlert={showAlert} email={me?.email} />}
    </div>
  );
}

function GSPCredentials({ showAlert, canEdit }) {
  const [st, setSt] = useState(null);
  const [f, setF] = useState({ clientId: "", irpUsername: "", clientSecret: "", irpPassword: "", sandbox: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    fetch(`${API_BASE}/api/irn/credentials`, { headers: auth() }).then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { const s = ok ? d : {}; setSt(s); setF({ clientId: s.clientId || "", irpUsername: s.irpUsername || "", clientSecret: "", irpPassword: "", sandbox: s.sandbox ?? true }); })
      .catch(() => setSt({}));
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.clientId.trim() || !f.irpUsername.trim()) { setErr("Client ID and IRP username are required."); return; }
    if (!st?.configured && (!f.clientSecret || !f.irpPassword)) { setErr("Enter the client secret and IRP password for the first setup."); return; }
    setBusy(true);
    try {
      const body = { clientId: f.clientId.trim(), irpUsername: f.irpUsername.trim(), sandbox: f.sandbox };
      if (f.clientSecret) body.clientSecret = f.clientSecret;   // only when changed — GET returns a masked value
      if (f.irpPassword) body.irpPassword = f.irpPassword;
      const r = await fetch(`${API_BASE}/api/irn/credentials`, { method: "PUT", headers: json(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save credentials");
      showAlert?.("GSP credentials saved", "success"); load();
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  if (!st) return <Skel h={360} r={26} />;
  return (
    <div className="v2ac-stack">
      <section className={`v2-card v2ac-status ${st.configured ? "is-good" : ""}`}>
        <Duo name="gst" size={40} />
        <div><strong>{st.configured ? "e-Invoicing is set up" : "e-Invoicing isn't set up yet"}</strong>
          <span>{st.configured ? `Connected to the ${st.sandbox ? "sandbox (test)" : "production"} IRP. Generate IRNs from an invoice.` : "Add the credentials from your GST Suvidha Provider to generate IRNs and QR codes on B2B invoices."}</span></div>
        {st.configured && <span className={`v2-status ${st.sandbox ? "v2-status--warn" : "v2-status--good"}`}>{st.sandbox ? "Sandbox" : "Production"}</span>}
      </section>
      <form className="v2-card v2ac-section" onSubmit={save}>
        <header><h3>GSP credentials</h3><span>Stored encrypted. Secrets are never shown again after saving.</span></header>
        <fieldset className="v2ac-fs v2-form" disabled={!canEdit}>
          <Field label="Environment">
            <Segmented ariaLabel="Environment" value={f.sandbox ? "sandbox" : "production"} onChange={(v) => setF((p) => ({ ...p, sandbox: v === "sandbox" }))} options={[{ value: "sandbox", label: "Sandbox (test)" }, { value: "production", label: "Production" }]} />
          </Field>
          {!f.sandbox && st.sandbox !== false && <Banner tone="warn">Production IRNs are legally binding. Test in sandbox first.</Banner>}
          <div className="v2-form-row">
            <Field label="Client ID"><input className="v2-input v2rp-mono" autoComplete="off" value={f.clientId} onChange={(e) => setF((p) => ({ ...p, clientId: e.target.value }))} /></Field>
            <Field label="Client secret" hint={st.clientSecret ? `Saved ${st.clientSecret.slice(-6)} — leave blank to keep` : undefined}>
              <input className="v2-input v2rp-mono" type="password" autoComplete="new-password" value={f.clientSecret} onChange={(e) => setF((p) => ({ ...p, clientSecret: e.target.value }))} placeholder={st.clientSecret ? "••••••••" : ""} /></Field>
          </div>
          <div className="v2-form-row">
            <Field label="IRP username"><input className="v2-input" autoComplete="off" value={f.irpUsername} onChange={(e) => setF((p) => ({ ...p, irpUsername: e.target.value }))} /></Field>
            <Field label="IRP password" hint={st.configured ? "Leave blank to keep the saved password" : undefined}>
              <input className="v2-input" type="password" autoComplete="new-password" value={f.irpPassword} onChange={(e) => setF((p) => ({ ...p, irpPassword: e.target.value }))} placeholder={st.configured ? "••••••••" : ""} /></Field>
          </div>
          <p className="v2-row-sub">Only B2B invoices can be submitted for now — export and SEZ need extra fields that aren't supported yet.</p>
          {err && <Banner>{err}</Banner>}
          {canEdit && <div className="v2ac-actions"><button type="submit" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : "Save credentials"}</button></div>}
        </fieldset>
      </form>
    </div>
  );
}

const strength = (p) => {
  if (!p) return 0;
  let s = 0; if (p.length >= 8) s++; if (p.length >= 12) s++; if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++; if (/\d/.test(p)) s++; if (/[^\w]/.test(p)) s++;
  return Math.min(4, Math.max(1, s));
};
function Security({ showAlert, email }) {
  const [f, setF] = useState({ cur: "", next: "", again: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const s = strength(f.next);
  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.cur || !f.next) { setErr("Fill in your current and new password."); return; }
    if (f.next.length < 6) { setErr("New password must be at least 6 characters."); return; }
    if (f.next !== f.again) { setErr("The new passwords don't match."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth/changepassword`, { method: "PUT", headers: json(), body: JSON.stringify({ currentPassword: f.cur, newPassword: f.next }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't change password");
      setF({ cur: "", next: "", again: "" }); showAlert?.("Password changed", "success");
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <div className="v2ac-stack v2ac-narrow">
      <form className="v2-card v2ac-section" onSubmit={save}>
        <header><h3>Change password</h3><span>{email ? `For ${email}` : "For your sign-in"}</span></header>
        <div className="v2-form">
          <input type="email" autoComplete="username" value={email || ""} readOnly hidden />
          <Field label="Current password"><input className="v2-input" type={show ? "text" : "password"} autoComplete="current-password" value={f.cur} onChange={(e) => setF((p) => ({ ...p, cur: e.target.value }))} /></Field>
          <Field label="New password">
            <input className="v2-input" type={show ? "text" : "password"} autoComplete="new-password" value={f.next} onChange={(e) => setF((p) => ({ ...p, next: e.target.value }))} />
            {f.next && <div className={`v2ac-meter v2ac-meter--${s}`} aria-label={`Strength: ${["", "weak", "fair", "good", "strong"][s]}`}><i /><i /><i /><i /><span>{["", "Weak", "Fair", "Good", "Strong"][s]}</span></div>}
          </Field>
          <Field label="Confirm new password"><input className="v2-input" type={show ? "text" : "password"} autoComplete="new-password" value={f.again} onChange={(e) => setF((p) => ({ ...p, again: e.target.value }))} /></Field>
          <div className="v2-inset"><Switch id="v2ac-show" checked={show} onChange={setShow} label="Show passwords" /></div>
          {err && <Banner>{err}</Banner>}
          <div className="v2ac-actions"><button type="submit" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Updating…" : "Update password"}</button></div>
        </div>
      </form>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BANK DETAILS
// ══════════════════════════════════════════════════════════════════════════════
export function V2Bank({ showAlert }) {
  usePageTitle("Bank details");
  const { bank, getBankDetails } = useContext(bankContext);
  const { hasPermission } = useContext(userContext);
  const canEdit = hasPermission("dashboard", "write");
  const [loaded, setLoaded] = useState(false);
  const blank = { bankName: "", branchName: "", accountNumber: "", ifscCode: "", upiId: "" };
  const [f, setF] = useState(blank);
  const [confirmAcc, setConfirmAcc] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reveal, setReveal] = useState(false);
  useEffect(() => { Promise.resolve(getBankDetails?.()).finally(() => setLoaded(true)); }, []); // eslint-disable-line
  const savedB = useMemo(() => ({ bankName: bank?.bankName || "", branchName: bank?.branchName || "", accountNumber: bank?.accountNumber || "", ifscCode: bank?.ifscCode || "", upiId: bank?.upiId || "" }), [bank]);
  useEffect(() => { setF(savedB); setConfirmAcc(""); }, [savedB]);
  const has = !!savedB.accountNumber;
  const accChanged = f.accountNumber !== savedB.accountNumber;
  const dirty = JSON.stringify(f) !== JSON.stringify(savedB);
  const set = (k) => (e) => { let v = e.target.value; if (k === "ifscCode") v = v.toUpperCase().replace(/\s/g, ""); if (k === "accountNumber") v = v.replace(/\s/g, ""); setF((p) => ({ ...p, [k]: v })); };
  const mask = (a) => (a ? `•••• ${a.slice(-4)}` : "•••• ••••");
  const show = (a) => (a ? a.replace(/(.{4})/g, "$1 ").trim() : "");

  const save = async () => {
    setErr("");
    if (!f.bankName.trim() || !f.branchName.trim() || !f.accountNumber || !f.ifscCode) { setErr("Bank, branch, account number and IFSC are required."); return; }
    if (!/^\d{9,18}$/.test(f.accountNumber)) { setErr("Account number should be 9–18 digits."); return; }
    if (accChanged && confirmAcc !== f.accountNumber) { setErr("Re-enter the account number to confirm it."); return; }
    if (!RX.ifsc.test(f.ifscCode)) { setErr("IFSC should look like HDFC0001234 (11 characters, 5th is 0)."); return; }
    if (f.upiId && !RX.upi.test(f.upiId)) { setErr("UPI ID should look like name@bank."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/bank/addbankdetails`, { method: "POST", headers: json(), body: JSON.stringify({ ...f, bankName: f.bankName.trim(), branchName: f.branchName.trim(), upiId: f.upiId.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't save bank details");
      showAlert?.("Bank details saved", "success"); await getBankDetails?.();
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };

  return (
    <div className="v2p v2ac">
      <PageHead eyebrow="Account" title="Bank details" sub="Printed on your invoices and the client portal so clients can pay you." />
      {!loaded ? <Skel h={420} r={26} /> : (
        <div className="v2ac-bank">
          <div className="v2ac-bank-side">
            <div className="v2ac-card" aria-label="Bank card preview">
              <div className="v2ac-card-top"><span>{f.bankName || "Your bank"}</span><Glyph name="bank" size={22} /></div>
              <div className="v2ac-card-num">{reveal ? (show(f.accountNumber) || "0000 0000 0000") : mask(f.accountNumber)}</div>
              <div className="v2ac-card-foot">
                <div><span>IFSC</span><b>{f.ifscCode || "—"}</b></div>
                <div><span>Branch</span><b>{f.branchName || "—"}</b></div>
              </div>
            </div>
            {f.accountNumber && <button type="button" className="v2k-link" onClick={() => setReveal((r) => !r)}>{reveal ? "Hide account number" : "Show account number"}</button>}
            {f.upiId && RX.upi.test(f.upiId) && <div className="v2ac-upi"><Duo name="rupee" size={30} /><div><span>UPI</span><b>{f.upiId}</b></div></div>}
            <p className="v2-row-sub">{has ? "Clients see these details on every invoice." : "No bank details yet — invoices print without payment details."}</p>
          </div>
          <section className="v2-card v2ac-section">
            <header><h3>{has ? "Account" : "Add your account"}</h3><span>One account per company</span></header>
            <fieldset className="v2ac-fs v2-form" disabled={!canEdit}>
              <div className="v2-form-row">
                <Field label="Bank name"><input className="v2-input" value={f.bankName} onChange={set("bankName")} placeholder="e.g. HDFC Bank" /></Field>
                <Field label="Branch"><input className="v2-input" value={f.branchName} onChange={set("branchName")} placeholder="e.g. Connaught Place" /></Field>
              </div>
              <div className="v2-form-row">
                <Field label="Account number"><input className="v2-input v2rp-mono" inputMode="numeric" autoComplete="off" value={f.accountNumber} onChange={set("accountNumber")} /></Field>
                {accChanged && f.accountNumber
                  ? <Field label="Re-enter account number" hint={confirmAcc && confirmAcc !== f.accountNumber ? "Doesn't match yet" : confirmAcc ? "Matches" : undefined}>
                    <input className={`v2-input v2rp-mono${confirmAcc && confirmAcc !== f.accountNumber ? " is-bad" : ""}`} inputMode="numeric" autoComplete="off" value={confirmAcc} onChange={(e) => setConfirmAcc(e.target.value.replace(/\s/g, ""))} onPaste={(e) => e.preventDefault()} /></Field>
                  : <Field label="IFSC"><input className="v2-input v2rp-mono" maxLength={11} value={f.ifscCode} onChange={set("ifscCode")} placeholder="HDFC0001234" /></Field>}
              </div>
              {accChanged && f.accountNumber && <Field label="IFSC"><input className="v2-input v2rp-mono" maxLength={11} value={f.ifscCode} onChange={set("ifscCode")} placeholder="HDFC0001234" /></Field>}
              <Field label="UPI ID" optional hint="Adds a UPI option for clients"><input className="v2-input" value={f.upiId} onChange={set("upiId")} placeholder="yourname@okhdfcbank" /></Field>
              {err && <Banner>{err}</Banner>}
              {!canEdit && <Banner tone="warn">You can view bank details but not change them.</Banner>}
            </fieldset>
          </section>
        </div>)}
      <SaveBar dirty={dirty && canEdit} busy={busy} onSave={save} onReset={() => { setF(savedB); setConfirmAcc(""); setErr(""); }} label={has ? "Save changes" : "Save bank details"} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TEAM
// ══════════════════════════════════════════════════════════════════════════════
// Mirrors backend/middleware/requireRole.js PERMISSIONS (descriptions only — the server enforces).
const ROLES = [
  { k: "admin", label: "Admin", desc: "Everything, including team and company settings" },
  { k: "accountant", label: "Accountant", desc: "Invoices, ledgers, bills, GST, TDS, expenses · reads reports and clients" },
  { k: "sales", label: "Sales", desc: "Clients and invoices" },
  { k: "inventory", label: "Inventory", desc: "Stock, products, purchases and suppliers" },
  { k: "hr", label: "HR", desc: "Staff, attendance and payroll" },
  { k: "viewer", label: "Viewer", desc: "Read-only access to everything except settings" },
];
const ROLE = Object.fromEntries([{ k: "owner", label: "Owner", desc: "Created the company account" }, ...ROLES].map((r) => [r.k, r]));
const MSTATUS = { active: ["Active", "good"], inactive: ["Inactive", "neutral"], pending: ["Invite pending", "warn"] };

export function V2Team({ showAlert }) {
  usePageTitle("Team");
  const { user } = useContext(userContext);
  const [members, setMembers] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [roleFor, setRoleFor] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind: 'status'|'remove', m }
  const addBtn = useRef(null);

  const load = useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/api/auth/team`, { headers: auth() }); const d = await r.json(); if (!r.ok) throw new Error(d.error || "Couldn't load your team"); setMembers(Array.isArray(d) ? d : []); }
    catch (x) { setErr(x.message); setMembers([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const call = async (url, method, body, ok) => {
    const r = await fetch(`${API_BASE}${url}`, { method, headers: json(), body: body ? JSON.stringify(body) : undefined });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || "Something went wrong");
    showAlert?.(d.message || ok, "success"); load();
  };
  const resend = async (m) => { try { await call(`/api/auth/team/${m._id}/resend-invite`, "POST", null, `Invite re-sent to ${m.email}`); } catch (x) { showAlert?.(x.message, "danger"); } };

  const all = members || [];
  const count = (s) => all.filter((m) => (m.status || "active") === s).length;
  const shown = all.filter((m) => !q.trim() || `${m.name} ${m.email} ${m.role}`.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (a.role === "owner" ? -1 : b.role === "owner" ? 1 : (a.name || a.email).localeCompare(b.name || b.email)));
  const isMe = (m) => user?._id && (m._id === user._id);

  return (
    <div className="v2p">
      <PageHead eyebrow="Account" title="Team" sub="Who can sign in to your company, and what each person can do."
        actions={<button ref={addBtn} type="button" className="v2-btn v2-btn--primary" onClick={() => setAdding(true)}><Glyph name="plus" size={17} stroke={2.2} /> Add member</button>} />
      <div className="v2ac-pills">
        <span><b>{members ? all.length : "—"}</b> members</span>
        <span><i className="is-good" /><b>{count("active")}</b> active</span>
        {count("pending") > 0 && <span><i className="is-warn" /><b>{count("pending")}</b> invited</span>}
        {count("inactive") > 0 && <span><i /><b>{count("inactive")}</b> inactive</span>}
      </div>
      {err && <Banner>{err}</Banner>}
      <div className="v2i-toolbar"><div className="v2-field--search v2i-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or role" aria-label="Search team" /></div></div>
      <section className="v2-card v2i-card">
        {!members ? <div className="v2-list">{[0, 1, 2].map((i) => <Skel key={i} h={62} r={16} />)}</div>
          : !shown.length ? <div className="v2c-empty"><Duo name="people" size={48} /><strong>{all.length ? "No one matches" : "Just you so far"}</strong><span>Invite your accountant or sales team and choose what they can access.</span></div>
          : <div className="v2i-rows">{shown.map((m) => {
            const [sl, st] = MSTATUS[m.status || "active"] || MSTATUS.active; const r = ROLE[m.role] || { label: m.role };
            const locked = m.role === "owner" || isMe(m);
            return (
              <div key={m._id} className={`v2cn-row v2ac-member${m.status === "inactive" ? " is-dim" : ""}`}>
                <div className="v2cn-open v2ac-member-main">
                  <span className="v2-avatar v2-avatar--client" style={{ width: 42, height: 42, fontSize: 14.5 }}>{ini(m.name || m.email)}</span>
                  <span className="v2-row-main"><span className="v2-row-title">{m.name || m.email}{isMe(m) && <em className="v2ac-you">You</em>}</span><span className="v2-row-sub">{m.name ? m.email : `Added ${fmtDate(m.createdAt)}`}</span></span>
                  <span className="v2ac-role" title={r.desc}>{r.label}</span>
                  <span className="v2cn-end"><span className={`v2-status v2-status--${st}`}>{sl}</span></span>
                </div>
                {locked ? <span className="v2ac-lock" title={m.role === "owner" ? "The owner can't be changed" : "You can't change your own access"}><Glyph name="shield" size={16} /></span>
                  : <MenuButton className="v2-icon-btn v2i-more" label={`Options for ${m.name || m.email}`} width={230} items={[
                    { label: "Change role", glyph: "team", onSelect: () => setRoleFor(m) },
                    ...(m.status === "pending" ? [{ label: "Resend invite", glyph: "inbox", onSelect: () => resend(m) }] : []),
                    ...(m.status !== "pending" ? [{ label: m.status === "inactive" ? "Reactivate" : "Deactivate", glyph: m.status === "inactive" ? "check" : "clock", onSelect: () => setConfirm({ kind: "status", m }) }] : []),
                    "divider", { label: "Remove from team", glyph: "close", danger: true, onSelect: () => setConfirm({ kind: "remove", m }) }]} />}
              </div>);
          })}</div>}
      </section>

      <AddMember open={adding} originRef={addBtn} onClose={() => setAdding(false)} onDone={(msg) => { setAdding(false); showAlert?.(msg, "success"); load(); }} />
      <RoleSheet m={roleFor} onClose={() => setRoleFor(null)} onSave={async (role) => { await call(`/api/auth/team/${roleFor._id}/role`, "PUT", { role }, "Role updated"); setRoleFor(null); }} />
      <Sheet open={!!confirm} onClose={() => setConfirm(null)} width={440}
        title={!confirm ? "" : confirm.kind === "remove" ? `Remove ${confirm.m.name || confirm.m.email}?` : `${confirm.m.status === "inactive" ? "Reactivate" : "Deactivate"} ${confirm.m.name || confirm.m.email}?`}
        footer={<><button type="button" className="v2-btn" onClick={() => setConfirm(null)}>Cancel</button>
          <button type="button" className={`v2-btn ${confirm?.kind === "remove" || confirm?.m.status !== "inactive" ? "v2c-btn-danger" : "v2-btn--primary"}`} onClick={async () => {
            const { kind, m } = confirm;
            try {
              if (kind === "remove") await call(`/api/auth/team/${m._id}`, "DELETE", null, "Member removed");
              else await call(`/api/auth/team/${m._id}/status`, "PUT", { status: m.status === "inactive" ? "active" : "inactive" }, "Status updated");
              setConfirm(null);
            } catch (x) { setConfirm((c) => ({ ...c, error: x.message })); }
          }}>{!confirm ? "" : confirm.kind === "remove" ? "Remove" : confirm.m.status === "inactive" ? "Reactivate" : "Deactivate"}</button></>}>
        <p className="v2c-remove-text">{confirm?.kind === "remove" ? "They lose access immediately. Their invoices and records stay. This can't be undone — deactivate instead if they might come back."
          : confirm?.m.status === "inactive" ? "They'll be able to sign in again with their current role." : "They'll be signed out and can't sign in until reactivated. Nothing they created is removed."}</p>
        {confirm?.error && <Banner>{confirm.error}</Banner>}
      </Sheet>
    </div>
  );
}

function RolePicker({ value, onChange }) {
  return (
    <div className="v2ac-roles" role="radiogroup" aria-label="Role">
      {ROLES.map((r) => (
        <button key={r.k} type="button" role="radio" aria-checked={value === r.k} className={`v2ac-role-opt${value === r.k ? " is-on" : ""}`} onClick={() => onChange(r.k)}>
          <span className="v2ac-radio" aria-hidden="true" /><span><b>{r.label}</b><span>{r.desc}</span></span>
        </button>))}
    </div>
  );
}

function RoleSheet({ m, onClose, onSave }) {
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (m) { setRole(m.role); setErr(""); } }, [m]);
  return (
    <Sheet open={!!m} onClose={onClose} width={500} title="Change role" subtitle={m ? (m.name || m.email) : ""}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="v2-btn v2-btn--primary" disabled={busy || role === m?.role} onClick={async () => { setBusy(true); setErr(""); try { await onSave(role); } catch (x) { setErr(x.message); } finally { setBusy(false); } }}>{busy ? "Saving…" : "Save role"}</button></>}>
      <RolePicker value={role} onChange={setRole} />
      {err && <Banner>{err}</Banner>}
    </Sheet>
  );
}

function AddMember({ open, originRef, onClose, onDone }) {
  const [mode, setMode] = useState("invite");
  const [f, setF] = useState({ name: "", email: "", password: "", role: "accountant" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setMode("invite"); setF({ name: "", email: "", password: "", role: "accountant" }); setErr(""); setShow(false); } }, [open]);
  const go = async (e) => {
    e.preventDefault(); setErr("");
    if (!/^\S+@\S+\.\S+$/.test(f.email)) { setErr("Enter a valid email address."); return; }
    if (mode === "create" && (!f.name.trim() || f.password.length < 6)) { setErr("Add a name and a password of at least 6 characters."); return; }
    setBusy(true);
    try {
      const body = mode === "invite" ? { email: f.email.trim(), role: f.role } : { name: f.name.trim(), email: f.email.trim(), password: f.password, role: f.role };
      const r = await fetch(`${API_BASE}/api/auth/team/${mode}`, { method: "POST", headers: json(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || (mode === "invite" ? "Couldn't send the invite" : "Couldn't create the account"));
      onDone(d.message || (mode === "invite" ? `Invite sent to ${body.email}` : `${body.name} added`));
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} originRef={originRef} width={540} title="Add a team member"
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button><button type="submit" form="v2ac-add" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Working…" : mode === "invite" ? "Send invite" : "Create account"}</button></>}>
      <form id="v2ac-add" className="v2-form" onSubmit={go}>
        <Segmented ariaLabel="How to add" value={mode} onChange={setMode} options={[{ value: "invite", label: "Invite by email" }, { value: "create", label: "Create account" }]} />
        <p className="v2-row-sub">{mode === "invite" ? "They get an email link to set their own password." : "You set a password and share it with them. They should change it after signing in."}</p>
        {mode === "create" && <Field label="Name"><input className="v2-input" value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} /></Field>}
        <Field label="Email"><input className="v2-input" type="email" autoComplete="off" autoFocus value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} placeholder="name@company.com" /></Field>
        {mode === "create" && <Field label="Temporary password" hint="At least 6 characters">
          <span className="v2-input-wrap"><input className="v2-input" type={show ? "text" : "password"} autoComplete="new-password" value={f.password} onChange={(e) => setF((p) => ({ ...p, password: e.target.value }))} />
            <button type="button" className="v2ac-eye" onClick={() => setShow((s) => !s)}>{show ? "Hide" : "Show"}</button></span></Field>}
        <Field label="Role"><RolePicker value={f.role} onChange={(v) => setF((p) => ({ ...p, role: v }))} /></Field>
        {err && <Banner>{err}</Banner>}
      </form>
    </Sheet>
  );
}
